import { Controller } from "@hotwired/stimulus";

/**
 * Message Grading Preview Controller
 *
 * Fetches and displays message grading results in a read-only preview format.
 * Shows checkmarks for present elements, feedback for missing ones,
 * plus style issues and suggested rewrite.
 *
 * Usage:
 *   <div data-controller="message-grading-preview"
 *        data-message-grading-preview-url-value="/path/to/grades"
 *        data-message-grading-preview-message-value="Message text here">
 *     <div data-message-grading-preview-target="content"></div>
 *   </div>
 */
export default class extends Controller {
  static targets = ["content"];
  static values = {
    url: String,
    message: String,
    channel: { type: String, default: "all" },
  };

  connect() {
    if (this.messageValue && this.messageValue.length > 0) {
      this.fetchGrading();
    } else {
      this.showEmpty();
    }
  }

  async fetchGrading() {
    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken,
        },
        body: JSON.stringify({
          message: this.messageValue,
          channel: this.channelValue,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      this.renderResults(result);
    } catch (error) {
      console.error("Message grading preview error:", error);
      this.showError("Unable to load message quality check.");
    }
  }

  renderResults(result) {
    if (!result.success) {
      this.showError(result.error || "Grading failed");
      return;
    }

    const elements = result.elements || {};
    const elementConfig = [
      { key: "source", label: "Source", description: "Who is sending this message" },
      { key: "hazard", label: "Hazard", description: "What threat is occurring" },
      { key: "impacts", label: "Impacts", description: "Consequences to people" },
      { key: "location", label: "Location", description: "Where this is happening" },
      { key: "guidance", label: "Guidance", description: "What people should do" },
      { key: "time", label: "Time", description: "When to act" },
    ];

    let html = `
      <div class="ui ${this.scoreColor(result.overall_score)} message">
        <div class="header">
          <i class="${this.scoreIcon(result.overall_score)} icon"></i>
          Overall: ${this.scoreLabel(result.overall_score)}
        </div>
      </div>

      <h4 class="ui header">Message Elements</h4>
      <div class="ui relaxed divided list">
    `;

    for (const { key, label, description } of elementConfig) {
      const element = elements[key];
      if (element) {
        if (element.present) {
          html += `
            <div class="item">
              <i class="large green check circle icon"></i>
              <div class="content">
                <div class="header">${label}</div>
                <div class="description" style="color: #888;">${description}</div>
              </div>
            </div>
          `;
        } else {
          html += `
            <div class="item">
              <i class="large yellow exclamation circle icon"></i>
              <div class="content">
                <div class="header">${label}</div>
                <div class="description">${this.escapeHtml(element.feedback)}</div>
              </div>
            </div>
          `;
        }
      }
    }

    html += "</div>";

    // Style issues
    const styleIssues = result.style_issues || [];
    if (styleIssues.length > 0) {
      html += `
        <h4 class="ui header" style="margin-top: 1.5em;">Style Suggestions</h4>
        <div class="ui bulleted list">
          ${styleIssues.map((issue) => `<div class="item">${this.escapeHtml(issue)}</div>`).join("")}
        </div>
      `;
    }

    // Suggested rewrite
    if (result.suggested_rewrite) {
      html += `
        <h4 class="ui header" style="margin-top: 1.5em;">Suggested Rewrite</h4>
        <div class="ui blue segment">
          <p>${this.escapeHtml(result.suggested_rewrite)}</p>
        </div>
      `;
    }

    this.contentTarget.innerHTML = html;
  }

  scoreColor(score) {
    const colors = { good: "green", needs_work: "yellow", poor: "red" };
    return colors[score] || "grey";
  }

  scoreIcon(score) {
    const icons = { good: "check circle", needs_work: "exclamation circle", poor: "times circle" };
    return icons[score] || "question circle";
  }

  scoreLabel(score) {
    const labels = { good: "Good", needs_work: "Needs Work", poor: "Needs Improvement" };
    return labels[score] || "Unknown";
  }

  showEmpty() {
    this.contentTarget.innerHTML = `
      <div class="ui info message">
        <p>No message content to analyze.</p>
      </div>
    `;
  }

  showError(message) {
    this.contentTarget.innerHTML = `
      <div class="ui error message">
        <i class="exclamation triangle icon"></i>
        ${this.escapeHtml(message)}
      </div>
    `;
  }

  escapeHtml(text) {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  }

  get csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]');
    return meta ? meta.getAttribute("content") : "";
  }
}
